import React from 'react'
import { Region, type ComponentProps, type LayoutData } from '@enonic/react-components'
import EventPrototype from '/react4xp/components/prototype/Event'
import type { EventProps } from '/react4xp/components/prototype/Event/types'

// Processor output shape (cms/processors/event.ts) — input side only.
type Registration = {
	label: string
	url: string
	isExternal: boolean
	disabled: boolean
}

type EventLayoutData = {
	title?: string
	intro?: string
	image?: { src: string; alt?: string }
	locationTitle?: string
	datePrefix?: string
	dateLabel?: string
	timePrefix?: string
	timeLabel?: string
	doorsOpenNote?: string
	venuePrefix?: string
	venue?: string
	price?: string
	registration?: Registration
	recording?: Omit<Registration, 'disabled'>
	error?: string
}

export const EventLayout = ({ component, data, meta }: ComponentProps<LayoutData>) => {
	const d = (data || {}) as EventLayoutData

	// Typed against the prototype contract so a prop rename on the `prototype`
	// branch fails at `tsc --noEmit`, not in the browser (FR-7 / V-4).
	const eventProps: EventProps = {
		title: d.title,
		intro: d.intro,
		image: d.image,
		infoPanel: {
			// EventInfoPanelLocation.name is required — build conditionally.
			location: d.locationTitle ? { name: d.locationTitle } : undefined,
			date: d.dateLabel,
			time: d.timeLabel,
			doorsOpenNote: d.doorsOpenNote,
			venue: d.venue,
			price: d.price,
			// `url` -> `href`; label already i18n-resolved server-side (FR-3 / V-1, V-2).
			signUp: d.registration && {
				href: d.registration.url,
				label: d.registration.label,
				isExternal: d.registration.isExternal,
				disabled: d.registration.disabled,
			},
			// Recording sits under Sign up (Ana, I99X-399 2026-08-26) and stays enabled on
			// past events - that is exactly when a recording is worth offering.
			secondaryButton: d.recording && {
				href: d.recording.url,
				label: d.recording.label,
				isExternal: d.recording.isExternal,
			},
			// Prototype renders labels verbatim and its own defaults carry the colon
			// ('Dato:'), while the processor's prefixes are bare nouns — add the colon here.
			labels: {
				date: d.datePrefix ? `${d.datePrefix}:` : undefined,
				time: d.timePrefix ? `${d.timePrefix}:` : undefined,
				venue: d.venuePrefix ? `${d.venuePrefix}:` : undefined,
			},
		},
	}

	return (
		<>
			<EventPrototype {...eventProps} />
			<Region name="main" data={component?.regions?.main?.components || []} meta={meta} />
		</>
	)
}
